import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  Search,
  Filter,
  Download,
  Clock,
  User,
  Activity,
  ChevronLeft,
  ChevronRight,
  Eye,
  AlertCircle,
  CheckCircle,
  Settings,
  UserPlus,
  Trash2,
  Edit,
  LogIn,
  LogOut,
  CreditCard
} from 'lucide-react';
import toast from 'react-hot-toast';

const actionIcons = {
  login: LogIn,
  logout: LogOut,
  create: UserPlus,
  update: Edit,
  delete: Trash2,
  view: Eye,
  settings: Settings,
  payment: CreditCard,
  default: Activity
};

const actionColors = {
  login: 'bg-green-100 text-green-600',
  logout: 'bg-gray-100 text-gray-600',
  create: 'bg-blue-100 text-blue-600',
  update: 'bg-yellow-100 text-yellow-600',
  delete: 'bg-red-100 text-red-600',
  view: 'bg-purple-100 text-purple-600',
  settings: 'bg-orange-100 text-orange-600',
  payment: 'bg-green-100 text-green-600',
  default: 'bg-gray-100 text-gray-600'
};

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    action: searchParams.get('action') || '',
    resource: searchParams.get('resource') || '',
    dateRange: searchParams.get('dateRange') || ''
  });

  useEffect(() => {
    fetchLogs();
  }, [searchParams]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: searchParams.get('page') || 1,
        search: searchParams.get('search') || '',
        action: searchParams.get('action') || '',
        resource: searchParams.get('resource') || '',
        dateRange: searchParams.get('dateRange') || '',
        limit: 50
      };

      const response = await adminApi.getAuditLogs(params);
      setLogs(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      toast.error('Failed to load audit logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (filters.search) {
      newParams.set('search', filters.search);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage));
    setSearchParams(newParams);
  };

  const handleExport = async () => {
    try {
      const response = await adminApi.exportAuditLogs(filters);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pmp-audit-logs-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit logs downloaded');
    } catch (err) {
      toast.error('Failed to export audit logs');
      console.error(err);
    }
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const getActionIcon = (action) => {
    return actionIcons[action] || actionIcons.default;
  };

  const getActionColor = (action) => {
    return actionColors[action] || actionColors.default;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-500 mt-1">Track all system activities and changes</p>
        </div>
        <button onClick={handleExport} className="btn-secondary">
          <Download className="w-5 h-5 mr-2" />
          Export Logs
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className="input pl-10"
              />
            </div>
          </form>

          <div className="flex gap-2">
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="input"
            >
              <option value="">All Actions</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="view">View</option>
            </select>

            <select
              value={filters.resource}
              onChange={(e) => handleFilterChange('resource', e.target.value)}
              className="input"
            >
              <option value="">All Resources</option>
              <option value="tenant">Tenant</option>
              <option value="user">User</option>
              <option value="subscription">Subscription</option>
              <option value="invoice">Invoice</option>
              <option value="settings">Settings</option>
              <option value="support_ticket">Support Ticket</option>
            </select>

            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="input"
            >
              <option value="">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-header">Action</th>
                <th className="table-header">User</th>
                <th className="table-header">Resource</th>
                <th className="table-header">Description</th>
                <th className="table-header">IP Address</th>
                <th className="table-header">Timestamp</th>
                <th className="table-header text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <div className="inline-flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mr-2"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    No audit logs found
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const ActionIcon = getActionIcon(log.action);
                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className={`p-2 rounded-lg mr-3 ${getActionColor(log.action)}`}>
                            <ActionIcon className="w-4 h-4" />
                          </div>
                          <span className="font-medium text-gray-900 capitalize">
                            {log.action}
                          </span>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <p className="text-sm text-gray-900">{log.userName || 'System'}</p>
                            <p className="text-xs text-gray-500">{log.userEmail}</p>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                          {log.resource}
                        </span>
                      </td>
                      <td className="table-cell text-gray-600 max-w-xs truncate">
                        {log.description}
                      </td>
                      <td className="table-cell text-gray-500 font-mono text-xs">
                        {log.ipAddress || 'N/A'}
                      </td>
                      <td className="table-cell text-gray-500">
                        <div className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {new Date(log.createdAt).toLocaleString()}
                        </div>
                      </td>
                      <td className="table-cell text-right">
                        <button
                          onClick={() => handleViewDetails(log)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 50 + 1} to{' '}
              {Math.min(pagination.page * 50, pagination.total)} of {pagination.total} logs
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 text-sm text-gray-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Audit Log Details</h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Action</p>
                  <p className="font-medium text-gray-900 capitalize">{selectedLog.action}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Resource</p>
                  <p className="font-medium text-gray-900">{selectedLog.resource}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">User</p>
                  <p className="font-medium text-gray-900">{selectedLog.userName || 'System'}</p>
                  <p className="text-xs text-gray-500">{selectedLog.userEmail}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Timestamp</p>
                  <p className="font-medium text-gray-900">
                    {new Date(selectedLog.createdAt).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">IP Address</p>
                  <p className="font-medium text-gray-900 font-mono">
                    {selectedLog.ipAddress || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">User Agent</p>
                  <p className="font-medium text-gray-900 text-xs truncate">
                    {selectedLog.userAgent || 'N/A'}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">Description</p>
                <p className="text-gray-900">{selectedLog.description}</p>
              </div>

              {selectedLog.resourceId && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Resource ID</p>
                  <p className="font-mono text-sm text-gray-900">{selectedLog.resourceId}</p>
                </div>
              )}

              {selectedLog.metadata && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Additional Data</p>
                  <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.changes && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Changes</p>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500">
                          <th className="pb-2">Field</th>
                          <th className="pb-2">Previous</th>
                          <th className="pb-2">New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(selectedLog.changes).map(([field, values]) => (
                          <tr key={field}>
                            <td className="py-1 font-medium">{field}</td>
                            <td className="py-1 text-red-600">{values.old || '-'}</td>
                            <td className="py-1 text-green-600">{values.new || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;
